import { useQuery, useMutation } from '@tanstack/vue-query'
import { computed, toValue, type MaybeRefOrGetter } from 'vue'
import { queryKeys } from '../keys'
import { queryGet, queryPost, queryPut } from '../utils/request'

// Type definitions

export interface UserGrantRoleListing {
  id: number
  description: string | null
  type: string
  is_master: boolean
}

export interface UserGrantCompanyGroup {
  id: number
  description: string | null
  companies: number[]
}

export interface UserGrantListing {
  id: number
  company_group: UserGrantCompanyGroup | null
  role: UserGrantRoleListing
}

export interface UserWithGrantsResponse {
  id: number
  email: string
  is_active: boolean
  is_email_confirmed: boolean
  grants: UserGrantListing[]
}

export interface UserListingResponse {
  id: number
  email: string
  is_email_confirmed: boolean
  timezone: string | null
  grants: UserGrantListing[]
}

export interface UserListParams {
  limit?: number
  offset?: number
  role_type?: string
  tenant_id?: number
  is_master?: boolean
  [key: string]: any
}

export interface UserListResponse {
  items: UserListingResponse[]
  total: number | null
  limit: number
  offset: number
  has_more: boolean
}

export interface UserMePermission {
  resource: string | null
  action: string | null
}

export interface UserMeRole {
  id: number
  description: string | null
  type: string
  is_master: boolean
  permissions: UserMePermission[]
}

export interface UserMeCompanyGroup {
  id: number
  description: string
  companies: number[]
}

export interface UserMeGrant {
  id: number
  company_group: UserMeCompanyGroup | null
  role: UserMeRole
}

export interface UserMeTenant {
  id: number
  description: string
  api_access_enabled: boolean
  feature_flags: Record<string, unknown>
  grants: UserMeGrant[]
}

export interface UserMeResponse {
  id: number
  email: string
  is_email_confirmed: boolean
  timezone: string | null
  tenants: UserMeTenant[]
}

export interface ChangePasswordRequest {
  old_password: string
  password: string
}

export interface ResetPasswordData {
  token: string
  password: string
}

export interface SetFirstPasswordData {
  token: string
  password: string
}

// Cache configuration
const CACHE_TIMES = {
  USERS: 5 * 60 * 1000,
  USER_DETAIL: 2 * 60 * 1000,
  CURRENT_USER: 10 * 60 * 1000,
}

/**
 * QUERIES
 */

export const useCurrentUserQuery = () => {
  return useQuery({
    queryKey: queryKeys.currentUser(),
    queryFn: async (): Promise<UserMeResponse> => {
      return queryGet<UserMeResponse>('/v2/users/me')
    },
    staleTime: CACHE_TIMES.CURRENT_USER,
  })
}

export const useUsersQuery = (params: MaybeRefOrGetter<UserListParams> = {}) => {
  return useQuery({
    queryKey: computed(() => queryKeys.userList(toValue(params))),
    queryFn: async (): Promise<UserListResponse> => {
      const queryParams = toValue(params)
      return queryGet<UserListResponse>('/v2/users/', { params: queryParams })
    },
    staleTime: CACHE_TIMES.USERS,
  })
}

export const useUserQuery = (
  userId: MaybeRefOrGetter<number | null | undefined>,
  params: MaybeRefOrGetter<Record<string, any>> = {},
) => {
  return useQuery({
    queryKey: computed(() => {
      const id = toValue(userId)
      return id ? [...queryKeys.user(id), toValue(params)] : ['users', 'null']
    }),
    queryFn: async (): Promise<UserWithGrantsResponse> => {
      const id = toValue(userId)
      if (!id) throw new Error('User ID is required')
      return queryGet<UserWithGrantsResponse>(`/v2/users/${id}`, { params: toValue(params) })
    },
    staleTime: CACHE_TIMES.USER_DETAIL,
    enabled: computed(() => !!toValue(userId)),
  })
}

/**
 * MUTATIONS
 */

export const useChangePasswordMutation = () => {
  return useMutation({
    mutationFn: async (data: ChangePasswordRequest): Promise<void> => {
      return queryPost<void>('/v2/users/change-password', data)
    },
  })
}

export const useForgotPasswordMutation = () => {
  return useMutation({
    mutationFn: async (email: string): Promise<void> => {
      return queryPost<void>('/v2/users/forgot-password', { email })
    },
  })
}

export const useResetPasswordMutation = () => {
  return useMutation({
    mutationFn: async ({ token, password }: ResetPasswordData): Promise<void> => {
      return queryPost<void>('/v2/users/reset-password', { token, password })
    },
  })
}

export const useResendConfirmationMutation = () => {
  return useMutation({
    mutationFn: async (userId: number): Promise<void> => {
      return queryPost<void>(`/v2/users/${userId}/confirmation/resend`, {})
    },
  })
}

export const useSetFirstPasswordMutation = () => {
  return useMutation({
    mutationFn: async (data: SetFirstPasswordData): Promise<void> => {
      return queryPut<void>('/noauth/accounts/users/password/token', data)
    },
  })
}
